import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useGetCustomersQuery } from 'services/customers'
import { setCustomer } from 'state/customer'
import { clearContact } from 'state/customer/customerContactsSlice'
import { hideFormModal } from 'state/ui'
import DialogModal from 'components/generic/modals/dialog-modal'
import DuplicatedCustomerModalContent from 'components/containers/customers/new-customer/DuplicatedCustomerModalContent'

/**
 * Custom hook for managing duplicate customer validation logic
 * @param {Object} options - Configuration options
 * @param {Function} options.onCloseModal - Function to call when validation modal is closed
 * @param {Function} options.onProceedWithNewCustomer - Function to call when user decides to create a new customer
 * @param {Function} options.onUseExistingCustomer - Custom handler for using an existing customer (optional)
 * @returns {Object} Hook interface
 */
const useDuplicateCustomerCheck = ({ onCloseModal, onProceedWithNewCustomer, onUseExistingCustomer }) => {
  const [showCustomerValidationModal, setShowCustomerValidationModal] = useState(false)
  const [customersFilter, setCustomersFilter] = useState({
    sort: {
      field: 'displayName',
      ascending: true,
    },
    search: '',
    filter: {},
  })

  const dispatch = useDispatch()

  const { data: customersFiltered, isLoading: isLoadingCustomers } = useGetCustomersQuery(
    { filter: JSON.stringify(customersFilter) },
    { skip: !customersFilter?.search },
  )

  // Effect to handle customer query results
  useEffect(() => {
    if (!customersFiltered) return

    if (customersFiltered.Items?.length > 0) {
      // If we found duplicates, show the modal
      setShowCustomerValidationModal(true)
    } else if (customersFilter.search) {
      // If we searched but found no duplicates, close the modal and proceed
      setShowCustomerValidationModal(false)

      // Use a slight delay to ensure state updates have processed
      setTimeout(() => {
        onProceedWithNewCustomer && onProceedWithNewCustomer()
      }, 100)
    }
  }, [customersFiltered])

  const handleUseExistingCustomer = selected => {
    setShowCustomerValidationModal(false)

    if (onUseExistingCustomer) {
      // Use custom handler if provided
      onUseExistingCustomer(selected)
    } else {
      // Default behavior
      dispatch(setCustomer(selected))
      dispatch(clearContact())
      dispatch(hideFormModal())
    }
  }

  const handleCreateNewCustomer = () => {
    setShowCustomerValidationModal(false)
    onProceedWithNewCustomer && onProceedWithNewCustomer()
  }

  const checkDuplicateCustomer = searchTerm => {
    if (searchTerm) {
      setCustomersFilter({
        ...customersFilter,
        search: searchTerm.trim(),
      })
      return true // Indicating check has been initiated
    }
    return false // Indicating no check was needed/performed
  }

  const handleCloseValidationModal = () => {
    setShowCustomerValidationModal(false)
    onCloseModal && onCloseModal()
  }

  const DuplicateCustomerModal = () => (
    <DialogModal
      open={showCustomerValidationModal}
      hideDividers
      title="Duplicate Customer"
      onClose={handleCloseValidationModal}
      onSave={handleCreateNewCustomer}
      buttonTitle="Create New Customer"
      secondaryButtonTitle="Go Back"
      dialogStyles={{
        '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
        '& .MuiDialogContent-root': { paddingBottom: 0 },
      }}
    >
      <DuplicatedCustomerModalContent
        customers={customersFiltered?.Items}
        onSelectCustomer={selected => {
          handleUseExistingCustomer(selected)
        }}
      />
    </DialogModal>
  )

  return {
    checkDuplicateCustomer,
    DuplicateCustomerModal,
    isLoadingCustomers,
  }
}

export default useDuplicateCustomerCheck
